<script>
    import { onMount } from "svelte";

    import Logo from "@propublica/pp-v5-shared/src/icons/propublica-logo.svg";
    import NewsletterSignup from "../NewsletterSignup";

    /**
     * Hides the newsletter signup box in the footer.
     * @type {boolean}
     */
    export let hideNewsletterSignup = false;

    /**
     * Optional props to pass through to the `NewsletterSignup` component. You
     * can pass an object with any of the following keys:
     *
     * - `source`
     * - `region`
     *
     * See `NewsletterSignup` for details on allowed values.
     *
     * Any keys that you omit fall back to `NewsletterSignup`'s default values
     * for these props, which are generally the right ones for news apps.
     *
     * @type {HeaderFooterNewsletterProps}
     */
    export let newsletterSignupProps = {};

    /**
     * URL to use for donate link in footer. If not specified, defaults to URL
     * defaults to URL with no tracking param (something we might update in the
     * future).
     * @type {string}
     */
    export let donateUrl = "https://www.propublica.org/donate/";

    /**
     * @typedef {object} FooterLink
     * @property {string} text
     * @property {string} href
     * @property {string} [andText] i.e. "[text](href) and [andText](andHref)"
     * @property {string} [andHref] see preceding
     * @property { {[htmlAttrName: string]: string} } [miscAttrs]
     */
    /**
     * @typedef {object} FooterLinkSection
     * @property {string} heading
     * @property {string} slug
     * @property {FooterLink[]} links
     */

    /** @type {FooterLinkSection[]} */
    const FOOTER_LINK_SECTIONS = [
        {
            heading: "Sites",
            slug: "sites",
            links: [
                { text: "ProPublica", href: "https://www.propublica.org/" },
                {
                    text: "Local Reporting Network",
                    href: "https://www.propublica.org/local-reporting-network/",
                },
                {
                    text: "Texas Tribune Partnership",
                    href: "https://www.propublica.org/texas/",
                },
                {
                    text: "The Data Store",
                    href: "https://www.propublica.org/datastore/",
                },
                {
                    text: "Electionland",
                    href: "https://www.propublica.org/electionland/",
                },
            ],
        },
        {
            heading: "Sections",
            slug: "sections",
            links: [
                { text: "Topics", href: "https://www.propublica.org/topics/" },
                { text: "Series", href: "https://www.propublica.org/series/" },
                { text: "Videos", href: "https://www.propublica.org/video/" },
                {
                    text: "News Apps",
                    href: "https://www.propublica.org/newsapps/",
                },
                {
                    text: "Get Involved",
                    href: "https://www.propublica.org/getinvolved/",
                },
                {
                    text: "The Nerd Blog",
                    href: "https://www.propublica.org/nerds/",
                },
                {
                    text: "@ProPublica",
                    href: "https://www.propublica.org/atpropublica/",
                },
                { text: "Events", href: "https://www.propublica.org/events/" },
            ],
        },
        {
            heading: "Info",
            slug: "info",
            links: [
                {
                    text: "About",
                    href: "https://www.propublica.org/about/",
                },
                {
                    text: "Board and Advisors",
                    href: "https://www.propublica.org/leadership/",
                },
                {
                    text: "Officers and Staff",
                    href: "https://www.propublica.org/staff/",
                },
                {
                    text: "Diversity",
                    href: "https://www.propublica.org/diversity/",
                },
                {
                    text: "Jobs",
                    href: "https://www.propublica.org/jobs/",
                    andText: "Fellowships",
                    andHref: "https://www.propublica.org/fellowships/",
                },
                {
                    text: "Media Center",
                    href: "https://www.propublica.org/media-center/",
                },
                {
                    text: "Reports",
                    href: "https://www.propublica.org/reports/",
                },
                {
                    text: "Impact",
                    href: "https://www.propublica.org/impact/",
                },
                {
                    text: "Awards",
                    href: "https://www.propublica.org/awards/",
                },
                {
                    text: "Corrections",
                    href: "https://www.propublica.org/corrections/",
                },
            ],
        },
        {
            heading: "Policies",
            slug: "policies",
            links: [
                {
                    text: "Code of Ethics",
                    href: "https://www.propublica.org/code-of-ethics/",
                },
                {
                    text: "Advertising Policy",
                    href: "https://www.propublica.org/advertising/",
                },
                {
                    text: "Privacy Policy",
                    href: "https://www.propublica.org/legal/",
                },
            ],
        },
        {
            heading: "Follow",
            slug: "follow",
            links: [
                {
                    text: "Newsletters",
                    href: "https://www.propublica.org/newsletters/",
                    miscAttrs: {
                        "data-pp-view": "",
                        "data-pp-action": "visit",
                    },
                },
                {
                    text: "iOS",
                    href: "https://itunes.apple.com/us/app/propublica/id355298887?mt=8",
                    andText: "Android",
                    andHref:
                        "https://play.google.com/store/apps/details?id=com.propublica&amp;hl=en",
                },
                {
                    text: "RSS Feed",
                    href: "https://www.propublica.org/feeds/propublica/main",
                },
            ],
        },
        {
            heading: "More",
            slug: "more",
            links: [
                {
                    text: "Send Us Tips",
                    href: "https://www.propublica.org/tips/",
                },
                {
                    text: "Steal Our Stories",
                    href: "https://www.propublica.org/steal-our-stories/",
                },
                {
                    text: "Browse via Tor",
                    // Following URL must be http, not https
                    href: "http://p53lf57qovyuvwsc6xnrppyply3vtqm7l6pcobkmyqsiofyeznfu5uqd.onion/",
                },
                {
                    text: "Contact Us",
                    href: "https://www.propublica.org/contact/",
                },
                {
                    text: "Donate",
                    href: donateUrl,
                    miscAttrs: {
                        "data-pp-view": "",
                        "data-pp-action": "visit classy",
                    },
                },
                {
                    text: "More Ways to Give",
                    href: "https://www.propublica.org/support/other-ways-to-give/",
                },
            ],
        },
    ];

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    let activeLinks = "";
    let linkHeadingClickable = false;

    /**
     * @param {string} sectionSlug
     */
    function setActiveLinks(sectionSlug) {
        // If screen is big, don't "secretly" change active link section
        // (could lead to surprise state if user later shrinks viewport)
        if (!linkHeadingClickable) return;

        if (activeLinks === sectionSlug) {
            activeLinks = "";
        } else {
            activeLinks = sectionSlug;
        }
    }

    onMount(() => {
        // Media query doesn't use `belowBreakpoint` from pp-v5-shared, because
        // that isn't necessarily configured in all environments. If we refactor
        // v5-shared utils (e.g. to use modules), then we could use that helper
        // here to avoid hardcoding 36em.
        const screenIsSmall = window.matchMedia("(max-width: 36em)");
        const setClickable = () => {
            linkHeadingClickable = screenIsSmall.matches;
        };
        setClickable();
        screenIsSmall.addEventListener("change", setClickable);

        // This almost certainly is unnecessary, but it's tidy to remove the
        // unused event listener if SiteFooter gets destroyed (can't imagine
        // when this might happen)
        return () => {
            screenIsSmall.removeEventListener("change", setClickable);
        };
    });
</script>

<footer
    class="site-footer hide-print"
    id="site-footer"
    data-pp-location="footer"
>
    {#if hideNewsletterSignup !== true}
        <div class="site-footer__section-newsletter">
            <div class="site-footer__newsletter-wrap">
                <strong class="site-footer__newsletter-heading"
                    >Stay informed with the Daily&nbsp;Digest.</strong
                >
                <div class="newsletter-signup__input-wrap">
                    <NewsletterSignup
                        {...newsletterSignupProps}
                        newsletterList="The Daily Digest"
                        placeholderText="Enter your email"
                        inputID="site-footer-signup"
                        placement="site-footer"
                    />
                </div>
            </div>
            <!-- end .site-footer__newsletter-wrap -->
        </div>
        <!-- end .site-footer__section-newsletter -->
    {/if}

    <div class="site-footer__wrap-outer">
        <div class="site-footer__wrap-inner">
            <!-- Logo -->

            <div class="site-footer__section-logo">
                <a
                    class="site-footer__wordmark"
                    href="https://www.propublica.org/"
                >
                    <Logo />
                </a>
                <strong class="site-footer__tagline"
                    >Investigative Journalism in the Public Interest</strong
                >
                <p class="site-footer__copyright">
                    © Copyright {currentYear} Pro Publica Inc.
                </p>
            </div>
            <!-- end .site-footer__section-logo -->

            <!-- Links -->

            <nav class="site-footer__section-links">
                {#each FOOTER_LINK_SECTIONS as section}
                    <!-- If we ever add a heading with whitespace, then change
                        the following logic (e.g. hyphenate for class name) -->
                    {@const sectionSlug = section.heading.toLowerCase()}
                    {@const ulId = `site-footer__links-list-${sectionSlug}`}
                    <div class="site-footer__links-{sectionSlug}">
                        <!--
                            Warning says only to use nonnegative tabindex when
                            interactive. Our logic handles this (but compiler
                            can't detect it)
                            svelte-ignore a11y-no-noninteractive-tabindex
                        -->
                        <strong
                            class="site-footer__links-heading"
                            tabindex={linkHeadingClickable ? 0 : null}
                            role={linkHeadingClickable ? "button" : null}
                            aria-controls={linkHeadingClickable ? ulId : null}
                            aria-expanded={linkHeadingClickable
                                ? activeLinks === sectionSlug
                                : null}
                            on:click={() => setActiveLinks(sectionSlug)}
                            on:keydown={(ev) => {
                                if (ev.key === " " || ev.key === "Enter") {
                                    setActiveLinks(sectionSlug);
                                }
                            }}>{section.heading}</strong
                        >
                        <ul
                            class="site-footer__links-list"
                            id={ulId}
                            class:hide-below-sm={activeLinks !== sectionSlug}
                        >
                            {#each section.links as link}
                                <li class="site-footer__link">
                                    <a href={link.href} {...link.miscAttrs}
                                        >{link.text}</a
                                    >
                                    {#if link.andText && link.andHref}
                                        and <a href={link.andHref}
                                            >{link.andText}</a
                                        >
                                    {/if}
                                </li>
                            {/each}
                        </ul>
                    </div>
                {/each}
            </nav>
            <!-- end .site-footer__section-links -->
        </div>
        <!-- end .site-footer__wrap-inner -->
    </div>
    <!-- end .site-footer__wrap-outer -->
</footer>

<style>.site-footer__section-newsletter {
  padding: var(--spacing2) 0;
}
:global(html.force-light-mode) :global(.text-dark) .site-footer__section-newsletter, :global(html.force-light-mode) .site-footer__section-newsletter {
  background: var(--cool-80);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-dark) .site-footer__section-newsletter, :global(html:not(.force-dark-mode)) .site-footer__section-newsletter {
    background: var(--cool-80);
  }
}
:global(html.force-dark-mode) :global(.text-dark) .site-footer__section-newsletter, :global(html.force-dark-mode) .site-footer__section-newsletter {
  background: var(--cool-70);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-dark) .site-footer__section-newsletter, :global(html:not(.force-light-mode)) .site-footer__section-newsletter {
    background: var(--cool-70);
  }
}
:global(html.force-light-mode) :global(.text-light) .site-footer__section-newsletter {
  background: var(--cool-70);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-light) .site-footer__section-newsletter {
    background: var(--cool-70);
  }
}
:global(html.force-dark-mode) :global(.text-light) .site-footer__section-newsletter {
  background: var(--cool-70);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-light) .site-footer__section-newsletter {
    background: var(--cool-70);
  }
}

.site-footer__newsletter-wrap {
  max-width: 1344px;
}
@media screen and (min-width: 0) {
  .site-footer__newsletter-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 36em) {
  .site-footer__newsletter-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 48em) {
  .site-footer__newsletter-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 60em) {
  .site-footer__newsletter-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 78em) {
  .site-footer__newsletter-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 88em) {
  .site-footer__newsletter-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 1439.551488px) {
  .site-footer__newsletter-wrap {
    margin: 0 auto;
  }
}

.site-footer__newsletter-wrap {
  max-width: 1344px;
  margin: 0 1.44rem;
}
@media screen and (min-width: 48em) {
  .site-footer__newsletter-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 1439.551488px) {
  .site-footer__newsletter-wrap {
    margin: 0 auto;
  }
}
@media screen and (min-width: 48em) {
  .site-footer__newsletter-wrap {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}
@media screen and (min-width: 78em) {
  .site-footer__newsletter-wrap {
    justify-content: center;
  }
}

.site-footer__newsletter-heading {
  color: var(--white);
  display: block;
  font-size: var(--scale1);
  line-height: var(--line-height-2);
}
@media screen and (max-width: 48em) {
  .site-footer__newsletter-heading {
    margin-bottom: var(--spacing-1);
  }
}
@media screen and (min-width: 48em) {
  .site-footer__newsletter-heading {
    width: calc((((100% - 14.5152rem) / 8) * 4) + 6.2208rem);
  }
}
@media screen and (min-width: 60em) {
  .site-footer__newsletter-heading {
    font-size: var(--scale2);
    width: calc((((100% - 22.8096rem) / 12) * 6) + 10.368rem);
  }
}
@media screen and (min-width: 78em) {
  .site-footer__newsletter-heading {
    margin-right: var(--spacing2);
    width: calc((((100% - 26.9568rem) / 14) * 6) + 10.368rem);
  }
}

.newsletter-signup__input-wrap {
  border-radius: 0.2em;
  font-size: var(--scale0);
  padding: var(--spacing-2) var(--spacing-1) var(--spacing-2) var(--spacing0);
  position: relative;
  margin: 0 auto;
}
:global(html.force-light-mode) :global(.text-dark) .newsletter-signup__input-wrap, :global(html.force-light-mode) .newsletter-signup__input-wrap {
  background: var(--white);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-dark) .newsletter-signup__input-wrap, :global(html:not(.force-dark-mode)) .newsletter-signup__input-wrap {
    background: var(--white);
  }
}
:global(html.force-dark-mode) :global(.text-dark) .newsletter-signup__input-wrap, :global(html.force-dark-mode) .newsletter-signup__input-wrap {
  background: transparent;
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-dark) .newsletter-signup__input-wrap, :global(html:not(.force-light-mode)) .newsletter-signup__input-wrap {
    background: transparent;
  }
}
:global(html.force-light-mode) :global(.text-dark) .newsletter-signup__input-wrap, :global(html.force-light-mode) .newsletter-signup__input-wrap {
  border: none;
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-dark) .newsletter-signup__input-wrap, :global(html:not(.force-dark-mode)) .newsletter-signup__input-wrap {
    border: none;
  }
}
:global(html.force-dark-mode) :global(.text-dark) .newsletter-signup__input-wrap, :global(html.force-dark-mode) .newsletter-signup__input-wrap {
  border: 1px solid var(--cool-20);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-dark) .newsletter-signup__input-wrap, :global(html:not(.force-light-mode)) .newsletter-signup__input-wrap {
    border: 1px solid var(--cool-20);
  }
}
:global(html.force-light-mode) :global(.text-light) .newsletter-signup__input-wrap, :global(html.force-light-mode) .newsletter-signup__input-wrap {
  background: transparent;
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-light) .newsletter-signup__input-wrap, :global(html:not(.force-dark-mode)) .newsletter-signup__input-wrap {
    background: transparent;
  }
}
:global(html.force-dark-mode) :global(.text-light) .newsletter-signup__input-wrap, :global(html.force-dark-mode) .newsletter-signup__input-wrap {
  background: transparent;
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-light) .newsletter-signup__input-wrap, :global(html:not(.force-light-mode)) .newsletter-signup__input-wrap {
    background: transparent;
  }
}
:global(html.force-light-mode) :global(.text-light) .newsletter-signup__input-wrap, :global(html.force-light-mode) .newsletter-signup__input-wrap {
  border: 1px solid var(--cool-20);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-light) .newsletter-signup__input-wrap, :global(html:not(.force-dark-mode)) .newsletter-signup__input-wrap {
    border: 1px solid var(--cool-20);
  }
}
:global(html.force-dark-mode) :global(.text-light) .newsletter-signup__input-wrap, :global(html.force-dark-mode) .newsletter-signup__input-wrap {
  border: 1px solid var(--cool-20);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-light) .newsletter-signup__input-wrap, :global(html:not(.force-light-mode)) .newsletter-signup__input-wrap {
    border: 1px solid var(--cool-20);
  }
}
@media screen and (min-width: 48em) {
  .newsletter-signup__input-wrap {
    margin: 0;
    width: calc((((100% - 14.5152rem) / 8) * 4) + 6.2208rem);
  }
}
@media screen and (min-width: 60em) {
  .newsletter-signup__input-wrap {
    width: calc((((100% - 22.8096rem) / 12) * 6) + 10.368rem);
  }
}
@media screen and (min-width: 78em) {
  .newsletter-signup__input-wrap {
    width: calc((((100% - 26.9568rem) / 14) * 6) + 10.368rem);
  }
}

.site-footer {
  clear: both;
  font-family: var(--fonts-sans);
  font-size: var(--scale-1);
  margin-top: var(--spacing3);
}
:global(html.force-light-mode) :global(.text-dark) .site-footer, :global(html.force-light-mode) .site-footer {
  background: var(--cool-10);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-dark) .site-footer, :global(html:not(.force-dark-mode)) .site-footer {
    background: var(--cool-10);
  }
}
:global(html.force-dark-mode) :global(.text-dark) .site-footer, :global(html.force-dark-mode) .site-footer {
  background: var(--cool-80);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-dark) .site-footer, :global(html:not(.force-light-mode)) .site-footer {
    background: var(--cool-80);
  }
}
:global(html.force-light-mode) :global(.text-light) .site-footer {
  background: var(--cool-80);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-light) .site-footer {
    background: var(--cool-80);
  }
}
:global(html.force-dark-mode) :global(.text-light) .site-footer {
  background: var(--cool-80);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-light) .site-footer {
    background: var(--cool-80);
  }
}
.site-footer a {
  color: inherit;
  text-decoration: none;
}
.site-footer a:focus, .site-footer a:hover {
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-decoration-thickness: 0.1em;
  text-underline-offset: 0.1em;
}
:global(.text-dark) .site-footer a:focus, .site-footer a:focus, :global(.text-dark) .site-footer a:hover, .site-footer a:hover {
  color: var(--color-accent-70);
}
:global(html.force-light-mode) :global(.text-light) .site-footer a:focus, :global(html.force-light-mode) :global(.text-light) .site-footer a:hover {
  color: var(--color-accent-30);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-light) .site-footer a:focus, :global(html:not(.force-dark-mode)) :global(.text-light) .site-footer a:hover {
    color: var(--color-accent-30);
  }
}
:global(html.force-dark-mode) :global(.text-light) .site-footer a:focus, :global(html.force-dark-mode) :global(.text-light) .site-footer a:hover {
  color: var(--color-accent-30);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-light) .site-footer a:focus, :global(html:not(.force-light-mode)) :global(.text-light) .site-footer a:hover {
    color: var(--color-accent-30);
  }
}

.site-footer__wrap-outer {
  max-width: 1344px;
}
@media screen and (min-width: 0) {
  .site-footer__wrap-outer {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 36em) {
  .site-footer__wrap-outer {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 48em) {
  .site-footer__wrap-outer {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 60em) {
  .site-footer__wrap-outer {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 78em) {
  .site-footer__wrap-outer {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 88em) {
  .site-footer__wrap-outer {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 1439.551488px) {
  .site-footer__wrap-outer {
    margin: 0 auto;
  }
}

.site-footer__wrap-outer {
  max-width: 1344px;
  margin: 0 1.44rem;
}
@media screen and (min-width: 48em) {
  .site-footer__wrap-outer {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 1439.551488px) {
  .site-footer__wrap-outer {
    margin: 0 auto;
  }
}

.site-footer__wrap-inner {
  display: flex;
  flex-direction: column-reverse;
  padding: var(--spacing1) 0;
}
@media screen and (min-width: 48em) {
  .site-footer__wrap-inner {
    display: grid;
    grid-gap: 2.0736rem;
    grid-template-areas: "logo links links links";
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
}
@media screen and (min-width: 78em) {
  .site-footer__wrap-inner {
    padding-left: calc((((100% - 26.9568rem) / 14) * 1) + 2.0736rem);
    padding-right: calc((((100% - 26.9568rem) / 14) * 1) + 2.0736rem);
  }
}

.site-footer__section-logo {
  font-size: var(--scale-2);
  grid-area: logo;
  line-height: var(--line-height-2);
}
@media screen and (max-width: 48em) {
  .site-footer__section-logo {
    margin: var(--spacing0) 0;
  }
}
.site-footer__section-logo > * + * {
  margin-top: var(--spacing-1);
}

.site-footer__wordmark {
  display: block;
  max-width: 20em;
}
:global(html.force-light-mode) :global(.text-dark) .site-footer__wordmark, :global(html.force-light-mode) .site-footer__wordmark {
  fill: var(--cool-80);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-dark) .site-footer__wordmark, :global(html:not(.force-dark-mode)) .site-footer__wordmark {
    fill: var(--cool-80);
  }
}
:global(html.force-dark-mode) :global(.text-dark) .site-footer__wordmark, :global(html.force-dark-mode) .site-footer__wordmark {
  fill: var(--cool-10);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-dark) .site-footer__wordmark, :global(html:not(.force-light-mode)) .site-footer__wordmark {
    fill: var(--cool-10);
  }
}
:global(html.force-light-mode) :global(.text-light) .site-footer__wordmark {
  fill: var(--cool-10);
}
@media (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-light) .site-footer__wordmark {
    fill: var(--cool-10);
  }
}
:global(html.force-dark-mode) :global(.text-light) .site-footer__wordmark {
  fill: var(--cool-10);
}
@media screen and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-light) .site-footer__wordmark {
    fill: var(--cool-10);
  }
}

.site-footer__tagline {
  display: block;
  font-family: var(--fonts-serif);
  margin-top: var(--spacing-2);
}

.site-footer__section-links {
  grid-area: links;
}
@media screen and (min-width: 36em) {
  .site-footer__section-links {
    column-count: 3;
    column-gap: 1rem;
  }
  .site-footer__section-links > * {
    break-inside: avoid;
  }
}
@media screen and (min-width: 48em) {
  .site-footer__section-links {
    grid-gap: 2.0736rem;
  }
}
@media screen and (max-width: 36em) {
  .site-footer__section-links > *:nth-child(1n+2) {
    border-top: 1px solid;
  }
  :global(html.force-light-mode) :global(.text-dark) .site-footer__section-links > *:nth-child(1n+2), :global(html.force-light-mode) .site-footer__section-links > *:nth-child(1n+2) {
    border-color: var(--cool-30);
  }
}
@media screen and (max-width: 36em) and (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-dark) .site-footer__section-links > *:nth-child(1n+2), :global(html:not(.force-dark-mode)) .site-footer__section-links > *:nth-child(1n+2) {
    border-color: var(--cool-30);
  }
}
@media screen and (max-width: 36em) {
  :global(html.force-dark-mode) :global(.text-dark) .site-footer__section-links > *:nth-child(1n+2), :global(html.force-dark-mode) .site-footer__section-links > *:nth-child(1n+2) {
    border-color: var(--cool-70);
  }
}
@media screen and (max-width: 36em) and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-dark) .site-footer__section-links > *:nth-child(1n+2), :global(html:not(.force-light-mode)) .site-footer__section-links > *:nth-child(1n+2) {
    border-color: var(--cool-70);
  }
}
@media screen and (max-width: 36em) {
  :global(html.force-light-mode) :global(.text-light) .site-footer__section-links > *:nth-child(1n+2) {
    border-color: var(--cool-70);
  }
}
@media screen and (max-width: 36em) and (prefers-color-scheme: light) {
  :global(html:not(.force-dark-mode)) :global(.text-light) .site-footer__section-links > *:nth-child(1n+2) {
    border-color: var(--cool-70);
  }
}
@media screen and (max-width: 36em) {
  :global(html.force-dark-mode) :global(.text-light) .site-footer__section-links > *:nth-child(1n+2) {
    border-color: var(--cool-70);
  }
}
@media screen and (max-width: 36em) and (prefers-color-scheme: dark) {
  :global(html:not(.force-light-mode)) :global(.text-light) .site-footer__section-links > *:nth-child(1n+2) {
    border-color: var(--cool-70);
  }
}

.site-footer__links-heading {
  display: block;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
@media screen and (max-width: 36em) {
  .site-footer__links-heading {
    padding: var(--spacing-1) 0;
  }
  .site-footer__links-heading:hover {
    cursor: pointer;
  }
}

.site-footer__links-list {
  margin-bottom: var(--spacing-1);
}</style>
